import { AlertCircle, Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { useState, type KeyboardEvent } from 'react'
import { useForm } from 'react-hook-form'

import { getFileUrl } from '@/clients/http'
import { useAuth } from '@/contexts/AuthContext'
import { useBusiness } from '@/contexts/BusinessContext'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'

interface LoginValues {
  email: string
  password: string
}

/** Staff sign-in: the store logo above a centred card on the white page. */
export const Login = () => {
  const { login } = useAuth()
  const { profile } = useBusiness()
  const [showPassword, setShowPassword] = useState(false)
  const [capsLock, setCapsLock] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    setValue,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({ defaultValues: { email: '', password: '' } })

  const storeName = profile?.name || 'IWS Commerce'
  const logo = profile?.logo_path ? getFileUrl(profile.logo_path) : '/logo.png'
  const host = profile?.custom_customer_domain || profile?.platform_customer_domain

  const onSubmit = handleSubmit(async (values) => {
    setFailure(null)
    try {
      await login({ email: values.email.trim(), password: values.password })
    } catch (e) {
      setFailure(e instanceof Error && e.message ? e.message : 'Sign-in failed. Please try again.')
      setValue('password', '')
      setFocus('password')
    }
  })

  const trackCapsLock = (e: KeyboardEvent<HTMLInputElement>) => setCapsLock(e.getModifierState('CapsLock'))

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-page p-4 sm:p-6">
      <div className="flex w-full max-w-[420px] flex-col items-center gap-6">
        <div className="flex items-center gap-2.5">
          <img src={logo} alt="" width={36} height={36} className="size-9 rounded-md bg-card object-contain" />
          <span className="text-heading-md text-ink">{storeName}</span>
        </div>

        <form
          onSubmit={onSubmit}
          noValidate
          aria-describedby={failure ? 'login-error' : undefined}
          className="w-full rounded-lg border border-border bg-card p-6 shadow-card sm:p-8"
        >
          <h1 className="text-heading-lg text-ink">Log in</h1>
          <p className="mt-1 text-sm text-text-2">Continue to {storeName} admin with your staff email and password.</p>

          {failure && (
            <div
              id="login-error"
              role="alert"
              className="mt-5 flex items-start gap-2 rounded-md border border-error-light bg-error-lighter px-3 py-2.5 text-sm text-error-dark"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>{failure}</span>
            </div>
          )}

          <div className="mt-5 flex flex-col gap-4">
            <div className="space-y-1">
              <Label htmlFor="email" className="font-normal text-ink">Email</Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-faint" aria-hidden="true" />
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  autoFocus
                  placeholder="you@yourstore.com"
                  className="pl-8"
                  aria-invalid={!!errors.email || !!failure}
                  {...register('email', {
                    required: 'Enter your email',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
                    onChange: () => setFailure(null),
                  })}
                />
              </div>
              {errors.email && <p className="text-sm text-error">{errors.email.message}</p>}
            </div>

            <div className="space-y-1">
              <Label htmlFor="password" className="font-normal text-ink">Password</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-faint" aria-hidden="true" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Your password"
                  className="pl-8 pr-9"
                  aria-invalid={!!errors.password || !!failure}
                  onKeyUp={trackCapsLock}
                  onKeyDown={trackCapsLock}
                  {...register('password', { required: 'Enter your password', onChange: () => setFailure(null) })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-1 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded-sm text-muted-foreground hover:bg-fill hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
              {errors.password && <p className="text-sm text-error">{errors.password.message}</p>}
              {capsLock && <p className="text-sm text-warning-dark">Caps Lock is on.</p>}
            </div>

            <Button type="submit" className="mt-1 w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner /> Logging in…
                </>
              ) : (
                'Log in'
              )}
            </Button>
          </div>
        </form>

        {host && (
          <a href={`https://${host}`} className="text-sm text-text-2 hover:text-ink hover:underline">
            Back to the store
          </a>
        )}
      </div>
    </div>
  )
}
